import {useParams} from "react-router-dom";

import LoadSpinner from "../../../components/Common/LoadSpinner";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {
    brokerListUpdateRequests,
    getUpdateRequestsFilterCount,
    deleteUpdateRequests,
    resendUpdateRequests
} from "../../../services/api/broker";
import SideMenu from "../../../components/Navigation/SideMenu";
import {AuthContext} from "../../../context/AuthContext";
import SelectDropdown from "../../../components/Input/SelectDropdown";
import {debounceTime, from, fromEvent} from "rxjs";
import {map} from "rxjs/operators";
import {FILTER_RELOAD} from "../../../services/events";
import {moment} from "../../../services/moment-extensions";
import LogoAvatar from "../../../components/Item/LogoAvatar";
import {ArrowUpRight} from "react-feather";
import TooltipWhite from "../../../components/Common/TooltipWhite";
import {Reminders} from "./BrokerUpdateRequests/Reminders";
import ModalReadMore from "../../../components/Modal/ModalReadMore";
import {getActionsByStatus} from "../../../services/constants/Broker/UpdateRequestsActions";
import {ACTIVE, CLOSED} from "../../../services/constants/Broker/UpdateRequestStatus";
import {mapUpdateRequestStatuses} from "../../../services/constants/Broker/UpdateRequestsListStatus";
import IconDropdownWithItems from "../../../components/Button/IconDropdownWithItems";
import Modal from "react-modal";
import ModalDeleteRequestUpdate from "../../../components/Modal/ModalDeleteRequestUpdate";
import ModalResendRequestUpdate from "../../../components/Modal/ModalResendRequestUpdate";
import SuccessToaster from "../../../components/Toasters/SuccessToaster";
import UpdateRequestsTable from "./BrokerUpdateRequests/UpdateRequestsTable";

export const BrokerUpdateRequests = () => {
    const {status, page, per_page} = useParams();
    const [updateRequestsFetched, setUpdateRequestsFetched] = useState(false);
    const [updateRequestsPagination, setUpdateRequestsPagination] = useState({
        total: 0,
        count: 0,
        per_page: parseInt(per_page, 10) || 10,
        current_page: parseInt(page, 10) || 1,
        total_pages: 1
    });
    const [updateRequestData, setUpdateRequestData] = useState([]);
    const updateRequestsFilterItems = useMemo(() => mapUpdateRequestStatuses(), []);
    const [activeFilter, setActiveFilter] = useState(updateRequestsFilterItems.find(item => item.key === status) || updateRequestsFilterItems[0]);
    const [activeFilterOptions, setActiveFilterOptions] = useState({
        status: activeFilter.key,
        page: updateRequestsPagination.current_page,
        per_page: updateRequestsPagination.per_page
    });

    const {baseUrl, company, ...user} = useContext(AuthContext);

    const [resendRequestOpen, setResendRequestOpen] = useState(false);
    const [deleteRequestOpen, setDeleteRequestOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [toastMessage, setToastMessage] = useState('');

    const handleToggleResendRequest = (item) => {
        setResendRequestOpen(!resendRequestOpen);
        setSelectedRequest(item);
    };

    const handleToggleDeleteRequest = (item) => {
        setDeleteRequestOpen(!deleteRequestOpen);
        setSelectedRequest(item);
    };

    const handleRequestUpdateAction = (item, selectedId) => {
        setToastMessage("");
        if (item.key === 'resend') {
            handleToggleResendRequest(selectedId);
        } else if (item.key === 'delete') {
            handleToggleDeleteRequest(selectedId);
        }
    };

    const handleResendAction = async (selectedItemId, message) => {
        const response = await resendUpdateRequests(selectedItemId, message);

        if (response.status) {
            setUpdateRequestData([]);
            setUpdateRequestsFetched(false);

            setToastMessage("Your request was successfully sent.");
        }
    };

    const handleDeleteAction = async (selectedItemId) => {
        const response = await deleteUpdateRequests(selectedItemId);

        if (response.status) {
            setUpdateRequestData([]);
            setUpdateRequestsFetched(false);

            setToastMessage("The request was deleted successfully.");
        }
    };

    const headers = [
        {data: 'Vendor', classes: "sl-px-3 sl-p-4 sl-pl-20 sl-border-none sl-text-left sl-w-45"},
        {data: 'Date requested', classes: "sl-px-3 sl-p-4 sl-text-left sl-w-34 sl-max-w-34 "},
        {data: 'Status', classes: "sl-px-3 sl-p-4 sl-text-left sl-w-29 sl-max-w-29 "},
        {data: 'Your note', classes: "sl-px-3 sl-p-4 sl-text-left sl-max-w-110 sl-grow sl-w-120 sl-max-w-120"},
        {data: 'Actions', classes: "sl-px-3 sl-p-4 sl-pr-20 sl-w-12 sl-max-w-26"}
    ]; // Table headers
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchUpdateRequests = async () => {
        if (!updateRequestData.length && !updateRequestsFetched)
            try {
                const {data, meta} = await brokerListUpdateRequests(activeFilterOptions);

                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                const transformedData = data.map((updateRequest) => ({
                    vendor: {
                        data: <div
                            className="sl-flex sl-gap-3 sl-justify-flex-start sl-align-center">
                            <LogoAvatar slug={updateRequest.vendor.slug} logo={updateRequest.vendor.favico}
                                        avatarClass="sl-h-6 sl-w-6"/>
                            <div className="sl-flex sl-flex-col sl-justify-flex-start ">
                                <div className="sl-ellipsis">
                                    <div className="sl-text-sm sl-font-medium sl-text-left sl-ellipsis-one-line">
                                        {updateRequest.vendor.name}
                                    </div>
                                </div>
                                <TooltipWhite disabled={updateRequest.requested_resource === 'profile'}
                                              content={updateRequest.requested_resource}>
                                    <div className="sl-h-auto">
                                        <div
                                            className={`sl-text-sm sl-font-normal sl-text-gray-base sl-text-left ${updateRequest.requested_resource !== 'profile' ? 'sl-cursor-pointer' : ''}`}>
                                            {updateRequest.type.description}
                                        </div>
                                    </div>
                                </TooltipWhite>
                            </div>
                        </div>,
                        classes: "sl-max-w-45 sl-w-45 sl-p-4 sl-pl-20 sl-border-none"
                    },
                    date: {
                        data: <div className="sl-flex sl-flex-col">
                            <div className="sl-text-sm sl-font-medium sl-text-left sl-text-gray-900">
                                {moment(updateRequest.created_at).toMediumDate()}
                            </div>
                            <div className="sl-text-sm sl-font-normal sl-text-gray-base sl-text-left">
                                {moment(updateRequest.created_at).toShortTime()}
                            </div>
                        </div>,
                        classes: "sl-p-4 sl-w-34 sl-max-w-34 "
                    },
                    status: {
                        data: <div className=" sl-flex sl-align-center sl-justify-flex-start">
                            <TooltipWhite disabled={updateRequest.reminders.length === 0} customClass={"sl-w-90"}
                                          content={<Reminders reminders={updateRequest.reminders}/>}>
                                <div className="sl-h-auto sl-flex sl-align-center sl-justify-flex-start">
                                    <div
                                        className={`sl-m-auto sl-text-sm sl-font-normal sl-text-gray-base sl-text-left ${updateRequest.reminders.length !== 0 ? 'sl-cursor-pointer' : ''}`}>
                                        <div className="sl-text-left">
                                            {(updateRequest.status === ACTIVE && updateRequest.reminders.length >= 5) &&
                                                <div
                                                    className="sl-badge sl-badge-dot sl-badge-sm sl-badge-icon-left sl-flex sl-gap-1-point-5 sl-badge-error">Failed</div>}
                                            {(updateRequest.status === ACTIVE && (updateRequest.reminders.length >= 0 && updateRequest.reminders.length < 5)) &&
                                                <div
                                                    className="sl-badge sl-badge-dot sl-badge-sm sl-badge-icon-left sl-flex sl-gap-1-point-5 sl-badge-warning">Pending</div>}
                                            {updateRequest.status === CLOSED &&
                                                <div
                                                    className="sl-badge sl-badge-dot sl-badge-sm sl-badge-icon-left sl-flex sl-gap-1-point-5 sl-text-success-700 sl-bg-success-50">Complete</div>}

                                        </div>
                                    </div>
                                </div>
                            </TooltipWhite>
                        </div>,
                        classes: "sl-p-4 sl-w-29 sl-max-w-29 "
                    },
                    message: {
                        data: <div className="sl-flex sl-flex-col sl-gap-1 sl-ellipsis">
                            <div
                                className="sl-text-left sl-text-xs sl-font-normal sl-text-gray-900 sl-ellipsis-two-line">
                                {updateRequest.message}
                            </div>
                            {(updateRequest.message.length > 126) && <div className="sl-text-left">
                                <div onClick={() => handleViewMoreClick(updateRequest.message)}
                                     className="sl-cursor-pointer sl-text-xs sl-font-medium sl-flex sl-gap-1 sl-align-center sl-btn-link-gray sl-text-gray-base">
                                    <div>View more details</div>
                                    <ArrowUpRight size={16}></ArrowUpRight>
                                </div>
                            </div>}
                        </div>,
                        classes: "sl-p-4 sl-w-120 sl-max-w-120"
                    },
                    actions: {
                        data: <div className="sl-max-w-26">
                            <IconDropdownWithItems customClass="sl-border-none" ready={updateRequest.status !== CLOSED}
                                                   onSelect={(action) => handleRequestUpdateAction(action, updateRequest.id)}
                                                   iconSize={20}
                                                   items={getActionsByStatus(updateRequest.reminders.length >= 5)}/>
                        </div>,
                        classes: "sl-p-4 sl-pr-20 sl-max-w-26 sl-flex sl-align-center sl-justify-center"
                    },
                }));
                setUpdateRequestData(transformedData);
                setUpdateRequestsPagination(meta.pagination);
                setActiveFilterOptions({
                    status: activeFilter.key,
                    page: meta.pagination.current_page,
                    per_page: meta.pagination.per_page
                })
                setUpdateRequestsFetched(true);
                setFilterReady(true);

            } catch (e) {
                console.error('Error fetching conversations:', e);
            }
    }
    const [isReadMoreVisible, setIsReadMoreVisible] = useState(false);
    const [modalContent, setModalContent] = useState('');

    // Function to show modal with specific content
    const handleViewMoreClick = (message) => {
        setModalContent(message);
        setIsReadMoreVisible(true);
    };

    // Function to close modal
    const handleCloseModal = () => {
        setIsReadMoreVisible(false);
        setModalContent('');
    };

    React.useEffect(() => {
            fetchUpdateRequests(activeFilterOptions);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeFilterOptions, updateRequestData, updateRequestsFetched]);

    React.useEffect(() => {
            setActiveFilterOptions({
                status: activeFilter.key,
                page: updateRequestsPagination.current_page,
                per_page: updateRequestsPagination.per_page
            })
            setUpdateRequestData([]);
            setUpdateRequestsFetched(false);

        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeFilter])

    const [filterReady, setFilterReady] = useState(false);

    useEffect(() => {
        const fetchUpdateRequestsFilterCount = async () => {
            return from(getUpdateRequestsFilterCount())
                .pipe(
                    map(response => response)
                )
                .subscribe(
                    result => {
                        let items = updateRequestsFilterItems.map((item) => {
                            const number = result[item.key];
                            const existingCount = /\(\d+\)$/.exec(item.text);
                            const countText = ` (${number})`;
                            if (existingCount) {
                                item.text = item.text.replaceAll(existingCount[0], countText);
                            } else {
                                item.text = item.text + countText;
                            }
                            return item;
                        });

                        setNewItemsCount(items);
                        setFilterReady(true);
                    },
                    error => {
                        console.error('Error fetching messages:', error);
                    }
                );
        };
        fetchUpdateRequestsFilterCount();

        const observable = fromEvent(document, FILTER_RELOAD);
        const eventObservable = observable.pipe(debounceTime(500));

        const subscription = eventObservable.subscribe(() => {
            fetchUpdateRequestsFilterCount();
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [updateRequestsFilterItems]);

    const handleChangePage = (page) => {
        setActiveFilterOptions({
            status: activeFilter.key,
            page: page,
            per_page: updateRequestsPagination.per_page
        })
        setUpdateRequestData([]);
        setUpdateRequestsFetched(false);
    }

    const [newItemsCount, setNewItemsCount] = useState(updateRequestsFilterItems);
    const handleSelectUpdateRequestFilterItem = (item) => {
        setActiveFilter(item)
    };

    return (
        <main className="sl-main sl-mt-8">
            <section className="sl-container sl-m-auto sl-my-8">
                <div className="sl-flex sl-gap-8" id="brokers-account-settings">
                    <div className="sl-col-span-2">
                        <SideMenu userData={user} baseUrl={baseUrl} company={company}/>
                    </div>

                    <div className="sl-flex sl-flex-col sl-col-span-10 sl-grow-1 sl-gap-6">
                        <div className="sl-flex sl-justify-space-between sl-align-flex-start">
                            <div className="sl-flex sl-flex-col sl-gap-1 sl-justify-flex-start sl-align-flex-start">
                                <h1 className="sl-heading-xs sl-font-bold sl-text-gray-900 sl-m-0">
                                    Vendor update requests
                                </h1>
                                <div className="sl-font-normal sl-text-sm sl-text-gray-700">
                                    Below, you can view the status of your requested updates from vendors.
                                </div>
                            </div>
                            <div className="sl-sl-align-center sl-justify-end">
                                <SelectDropdown activeFilter={activeFilter}
                                                buttonClass={"sl-bg-transparent sl-border-none sl-text-gray-500 sl-font-medium sl-text-sm"}
                                                contentClass={"sl-left-auto-i sl-w-55"}
                                                ready={filterReady} items={newItemsCount}
                                                onSelect={handleSelectUpdateRequestFilterItem}/>
                            </div>
                            <Modal
                                isOpen={resendRequestOpen}
                                onRequestClose={handleToggleResendRequest}
                                className="sl-modal-content sl-max-h-185"
                                overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                                <ModalResendRequestUpdate
                                    id={selectedRequest}
                                    handleResendAction={handleResendAction}
                                    onHandleToggle={handleToggleResendRequest}/>
                            </Modal>
                            <Modal
                                isOpen={deleteRequestOpen}
                                onRequestClose={handleToggleDeleteRequest}
                                className="sl-modal-content sl-max-h-185"
                                overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                                <ModalDeleteRequestUpdate
                                    id={selectedRequest}
                                    handleDeleteAction={handleDeleteAction}
                                    onHandleToggle={handleToggleDeleteRequest}/>
                            </Modal>
                        </div>
                        {updateRequestsFetched ? (
                            <>
                                {updateRequestData.length > 0 ? <UpdateRequestsTable
                                    onChangePage={handleChangePage}
                                    headers={headers}
                                    data={updateRequestData}
                                    pagination={updateRequestsPagination}
                                /> : <div className="sl-self-center sl-mt-3 sl-mb-21">
                                    No update requests found
                                </div>}
                            </>
                        ) : (
                            <div className="sl-self-center sl-mt-3 sl-mb-21">
                                <LoadSpinner/>
                            </div>
                        )}
                    </div>
                </div>

                {isReadMoreVisible && (
                    <Modal
                        isOpen={isReadMoreVisible}
                        onRequestClose={() => handleCloseModal()}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalReadMore
                            text={modalContent}
                            title="Note details"
                            onHandleClose={handleCloseModal}
                        />
                    </Modal>
                )}
            </section>
            {toastMessage.length > 0 &&
                <SuccessToaster label={toastMessage}/>}
        </main>
    );
};

export default BrokerUpdateRequests;
